import React from "react";
import { AlertTriangle, RefreshCw, Code } from "lucide-react";
import DirectoryTree from "./DirectoryTree";
import { CLASSES, REFRESH_ICON_SIZE, ALERT_ICON_SIZE } from "../../constants";
import { getEffectiveProjectPath } from "../../utils/pathUtils";

/**
 * Loading state component
 */
const LoadingState = () => (
  <div className="flex items-center justify-center h-32 text-neutral-500 dark:text-neutral-400">
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4 text-neutral-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
    <span className="text-xs">Loading project files...</span>
  </div>
);

/**
 * Error state component
 */
const ErrorState = ({ error, onRefresh }) => (
  <div className="p-3">
    <div className="text-red-500 dark:text-red-400 flex items-center text-xs">
      <AlertTriangle size={ALERT_ICON_SIZE} className="mr-2 flex-shrink-0" />
      <span>{error}</span>
    </div>
    <button
      onClick={onRefresh}
      className="mt-3 text-xs text-neutral-500 hover:text-neutral-400 dark:hover:text-neutral-400 flex items-center"
    >
      <RefreshCw size={REFRESH_ICON_SIZE} className="mr-1" />
      Try again
    </button>
  </div>
);

/**
 * Empty state component
 */
const EmptyState = () => (
  <div className="text-neutral-500 dark:text-neutral-400 p-3 text-xs">
    No files found in this project.
  </div>
);

/**
 * Main FileExplorer component
 * @param {Object} props - Component props
 * @param {Array} props.files - Array of file objects
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.fileError - Error message if any
 * @param {Function} props.onRefresh - Refresh handler
 * @param {Function} props.onFileSelect - File selection handler
 * @param {Function} props.onDirectoryToggle - Directory toggle handler
 * @param {Object} props.expandedFolders - Expanded folders state
 * @param {string} props.selectedFile - Currently selected file path
 * @param {Set} props.loadingDirectories - Set of loading directories
 * @param {string} props.loadingFile - Currently loading file path
 * @param {string} props.projectPath - Project root path
 * @returns {JSX.Element} The file explorer
 */
const FileExplorer = ({
  files,
  isLoading,
  fileError,
  onRefresh,
  onFileSelect,
  onDirectoryToggle,
  expandedFolders,
  selectedFile,
  loadingDirectories,
  loadingFile,
  projectPath,
}) => {
  const effectiveProjectPath = getEffectiveProjectPath(files, projectPath);

  return (
    <div className={CLASSES.fileExplorer} data-tour="file-explorer">
      {/* Header section */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center">
        <h3 className="text-sm font-medium text-ui-text-primary flex items-center">
          <Code size={16} className="mr-2" />
          Files
        </h3>
      </div>

      {/* File tree content */}
      <div className="flex-1 overflow-y-auto p-3 file-explorer-scrollbar">
        {isLoading && files.length === 0 ? (
          <LoadingState />
        ) : fileError ? (
          <ErrorState error={fileError} onRefresh={onRefresh} />
        ) : files.length === 0 ? (
          <EmptyState />
        ) : (
          <DirectoryTree
            files={files}
            expandedFolders={expandedFolders}
            onFileSelect={onFileSelect}
            onDirectoryToggle={onDirectoryToggle}
            selectedFile={selectedFile}
            loadingDirectories={loadingDirectories}
            loadingFile={loadingFile}
            rootPath={effectiveProjectPath}
            level={0}
          />
        )}
      </div>
    </div>
  );
};

export default FileExplorer;
